import { useMutation } from '@tanstack/react-query';
import { FORCE_FORBIDDEN_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ExportError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): ExportError {
  const e = new Error(stage) as ExportError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

function isoDate(now: Date = new Date()): string {
  // YYYY-MM-DD in UTC
  return now.toISOString().slice(0, 10);
}

export interface ExportResult {
  /** Filename used for the download, useful for tests and a11y status. */
  filename: string;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match?.[1] ?? fallback;
}

/**
 * Calls `GET /auth/account/export` and triggers a ZIP download. We request
 * `parseAs: 'blob'` so the openapi-fetch step-up middleware still intercepts a
 * 403 `step_up_required` (it runs in onResponse, before body parsing) and
 * replays the request after re-auth. The hook only sees eventual success or a
 * non-step-up error.
 */
export function useExportData() {
  const api = useApi();
  return useMutation<ExportResult, ExportError, void>({
    mutationFn: async () => {
      const { data, error, response } = await api.GET('/auth/account/export', {
        parseAs: 'blob',
        // A user-initiated action that happens to be a GET, so it opts in to
        // the global forbidden handler the mutation-only rule would skip.
        headers: { [FORCE_FORBIDDEN_HEADER]: '1' },
      });
      if (error || !data) throw toError('export_failed', response, error);
      const fallback = `sentinel-account-export-${isoDate()}.zip`;
      const filename = filenameFromDisposition(
        response?.headers.get('content-disposition') ?? null,
        fallback,
      );
      const blob = data as Blob;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Defer revocation to ensure the click had a chance to start the
        // download in all browsers.
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
      return { filename };
    },
  });
}
